import React, { FormEvent } from "react";
import { useEdges, useNodes, useReactFlow } from "reactflow";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare, faBolt, faLongArrowLeft, faPaperclip, faPaperPlane, faPlus } from "@fortawesome/free-solid-svg-icons";
import { GoPlug } from "react-icons/go";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { operationType } from "../../../services/types";
import { RootState } from "../../../redux/store";
import { allowedFileTypesDocSearch } from "../../nodes/children/knowledge/upload/docSearch";
import { Main } from "./style";
import { faComments, faPenToSquare } from "@fortawesome/free-regular-svg-icons";
import axios from "axios";
import { webURL } from "../../../services/API";
import { useSearchParams } from "react-router-dom";
import { allowedFileTypesFileUploader } from "../../nodes/children/knowledge/upload/files";

interface setupType { inputId: string, outputId: string, start: boolean, isInputHover: boolean, isOutputHover: boolean, isNodesUpdated: boolean, isCanGetOutput: boolean }

export function ChatProject(
   { projectId, setup, tabsChat, setTabsChat, setSetup, handleWorkflowRun, handleValuesFeatures }: 
   { projectId: any, setup: setupType, tabsChat: tabChatType[], setSetup: React.Dispatch<React.SetStateAction<setupType>>, setTabsChat: React.Dispatch<React.SetStateAction<tabChatType[]>>, handleWorkflowRun: () => Promise<void>, handleValuesFeatures: (values: any) => void }
) {
   const userAuth = useSelector((state: RootState) => state.user.data);
   const textareaRef = React.useRef<HTMLTextAreaElement>(null);
   const messagesRef = React.useRef<HTMLDivElement>(null);
   const [messageValue, setMessageValue] = React.useState('');
   const [titleTabValue, setTitleTabValue] = React.useState('');
   // const [isMultipleUsersChat, setIsMultipleUsersChat] = React.useState(false);
   const [isLoading, setIsLoading] = React.useState(true);
   // ---
   const [searchParams] = useSearchParams();
   const isExternalChat = React.useMemo(() => searchParams.get('isExternalChat') === 'true', [searchParams]);
   const { getNodes, setNodes } = useReactFlow();
   const nodes = useNodes<operationType>();
   const edges = useEdges();
   const findInput = nodes.find(node => node.id === setup.inputId);
   const findOutput = nodes.find(node => node.id === setup.outputId);
   const inputPlug = nodes.find(node => edges.some(edge => edge.source === findInput?.id && edge.target === node.id))
   const outputPlug = nodes.find(node => edges.some(edge => edge.target === findOutput?.id && edge.source === node.id));
   const fileBlock = nodes.find(node => node.data.action === 'fileUploader' || node.data.action === 'docSearch');

   // ---
   React.useEffect(() => {
      if (isExternalChat) {
         const inputId = searchParams.get('inputId');
         const outputId = searchParams.get('outputId');
         if (inputId && outputId) setSetup(setup => ({ ...setup, start: true, inputId, outputId }));
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);
   
   // get messages
   React.useEffect(() => {
      if (!tabsChat.length && setup.start && inputPlug) {
         (async function () {
            const data = (await axios.get(`${webURL}/chat/${projectId}?inputInx=${inputPlug?.data.index}&outputInx=${outputPlug?.data.index}`)).data;
            if (data) {
               setTabsChat(data.tabs);
            } else setTabsChat([{ id: 1, title: 'First Tab', isActive: true, items: []}]);
            setIsLoading(false);
            setTimeout(() => {
               if (messagesRef.current) messagesRef.current.scrollTo(0, messagesRef.current.scrollHeight);
            }, 50);
         })();
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [tabsChat, inputPlug, setup.start]);

   // auto height with textarea
   React.useLayoutEffect(() => {
      const textarea = textareaRef.current; 

      if (textareaRef.current) {
         if (messageValue) { // @ts-ignore
            textarea.style.height = 'auto'; // @ts-ignore
            textarea.style.height = `${textarea.scrollHeight}px`;
         } else {
            textareaRef.current.removeAttribute('style');
         }
      }
   }, [messageValue]);

   // Use effect to trigger handleWorkflowRun after nodes are updated
   React.useEffect(() => {
      const runWorkflow = async () => {
         if (setup.isNodesUpdated) {
            if (messagesRef.current) messagesRef.current.scrollTo(0, messagesRef.current.scrollHeight);
            await handleWorkflowRun();
            setSetup(setup => ({ ...setup, isNodesUpdated: false, isCanGetOutput: true }));
         }
      };
      runWorkflow();
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setup.isNodesUpdated]);
   // ----
   React.useEffect(() => {
      if (setup.isCanGetOutput) {
         const nodes = getNodes();
         const update = tabsChat.map(tab => {
            if (tab.isActive) return { ...tab, items: [...tab.items, { text: nodes.find(node => node.id === setup.outputId)?.data.value }] };
            else return tab;
         });
         setSetup(setup => ({ ...setup, isCanGetOutput: false }));
         setTabsChat(update);
         setTimeout(() => {
            if (messagesRef.current) messagesRef.current.scrollTo(0, messagesRef.current.scrollHeight);
            // save data
            axios.put(`${webURL}/chat/${projectId}`, { inputInx: inputPlug?.data.index, outputInx: outputPlug?.data.index, tabs: update });
         }, 50);
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setup.isCanGetOutput]);

   // ---
   const handleSendMessage = async (e: FormEvent) => {
      console.log(setup.isNodesUpdated, setup.isCanGetOutput, isLoading);

      e.preventDefault();
      if (setup.isNodesUpdated || setup.isCanGetOutput || isLoading) return;


      setNodes((nodes) => nodes.map((node) => {
         if (node.id === setup.inputId) return { ...node, data: { ...node.data, value: messageValue }};
         else return node;
      }));
      setSetup(setup => ({ ...setup, isNodesUpdated: true }));
      setMessageValue('');
      setTabsChat(tabs => tabs.map(tab => {
         if (tab.isActive) return { ...tab, items: [...tab.items, { isUser: true, text: messageValue }] };
         else return tab;
      }));
   }
   // ---
   const handleDropBlock = (type: 'input' | 'output', id: string) => {
      if (nodes.some(node => node.id === id && node.data.action === type)) {
         if (type === 'input') setSetup(setup => ({ ...setup, inputId: id, isInputHover: false }));
         else setSetup(setup => ({ ...setup, outputId: id, isOutputHover: false }));
         setTabsChat([]);
      } else {
         setSetup(setup => ({ ...setup, isInputHover: false, isOutputHover: false }));
      }
   }
   // ---
   const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
         const maxFileSize = 100 * 1024 * 1024; // 100 MB in bytes
         const allowedTypes = fileBlock?.data.action === 'docSearch' ? allowedFileTypesDocSearch : allowedFileTypesFileUploader;
         const validFiles = Array.from(event.target.files).filter(f => f.size <= maxFileSize && allowedTypes.includes(f.type));

         if (!validFiles.length) return;

         setNodes((nodes) =>
            nodes.map((node) => {
               if (node.id === fileBlock?.id) return { ...node, data: { ...node.data, value: { ...node.data.value, current: [...(node.data.value?.current || []), ...validFiles] }}};
               else return node;
            })
         );
      }
   }

   // ---
   const blocks = React.useMemo(() => [...nodes].filter(node => node.data.action === 'input' || node.data.action === 'output').sort((a, b) => {
      if (a.data.action === 'input' && b.data.action === 'output') return -1;
      if (a.data.action === 'output' && b.data.action === 'input') return 1;
      return a.data.name.localeCompare(b.data.name);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }), [edges]);

   // ---
   const tabActive = tabsChat.find(tab => tab.isActive);
   // const findWebhook = nodes.find(node => node.data.action === 'webhook');
   const findChatbotSequencer = nodes.find(node => node.data.action === 'sequencer');

   return (
      <div className="popup" onClick={ _=> handleValuesFeatures({ isChat: false }) }>
         <Main className={ isExternalChat ? 'external' : '' } onClick={ e => e.stopPropagation() } style={{ padding: !setup.start ? '30px' : '0' }}>
            { (isExternalChat && setup.start) || !isExternalChat ? <div 
               className="close" 
               onClick={ _=> { 
                  if (setup.start) setSetup(setup => ({ ...setup, start: false }));
                  else handleValuesFeatures({ isExport: true, isChat: false });
               }}
            >
               <FontAwesomeIcon icon={ faLongArrowLeft } />
            </div> : null}
            { !setup.start ? <div className="setup">
               <div className="title">
                  <h3>Setup Chat Assistant Interface</h3>
                  { !isExternalChat ? <div onClick={ _=> window.open(`/workflow?id=${projectId}&isExternalChat=true`, '_blank') }>
                     <FontAwesomeIcon icon={ faArrowUpRightFromSquare } />
                     <span>External chat</span>
                  </div> : null }
                  { !isExternalChat && findChatbotSequencer /* findWebhook */ ? <div 
                     // className={ isMultipleUsersChat ? 'active' : '' } 
                     onClick={ _=> {
                        /* if (!findWebhook.data.value?.isConversations) setIsMultipleUsersChat(true);
                        else */ window.open(`/conversations?projectId=${projectId}`, '_blank');
                     }}
                  >
                     <FontAwesomeIcon icon={ faComments } />
                     <span>Multiple Users Chat</span>
                  </div> : null }
               </div>
               <div className="flex">
                  <ul>
                     { blocks.map(item => {
                        const plug = nodes.find(node => {
                           return (item.data.action === 'input' && edges.some(edge => edge.source === item.id && edge.target === node.id)) || 
                           (item.data.action === 'output' && edges.some(edge => edge.target === item.id && edge.source === node.id)) 
                        });
                        return (
                           <li key={ item.id } className="block" onDragStart={ e => e.dataTransfer.setData('text/plain', item.id) } draggable>
                              <span>{ item.data.index }</span>
                              <span><GoPlug /> { plug?.data.index }</span>
                           </li>
                        );
                     })}
                     { !blocks.length ? <div className="empty">Workflow has no inputs or outputs.</div> : null }
                  </ul>
                  <div className="zone">{/* {
                     isMultipleUsersChat ? <div className="multiple">
                        <p>Are you sure of this action?</p>
                        <button
                           onClick={ _=> {
                              setNodes(nodes => nodes.map(node => {
                                 console.log(node.id, findWebhook?.id);
                                 if (node.id === findWebhook?.id) return { ...node, data: { ...node.data, value: { ...node.data.value, isConversations: true }}}; 
                                 else return node;
                              }));
                              setIsMultipleUsersChat(false);
                              window.open(`/conversations?projectId=${projectId}`, '_blank');
                           }}
                        >
                           Start
                        </button>
                        <button onClick={ _=> setIsMultipleUsersChat(false) }>Cancel</button>
                     </div> : <> */}
                        <div 
                           className={`block input ${ setup.isInputHover ? 'focus' : '' }`} 
                           onDrop={ e => handleDropBlock('input', e.dataTransfer.getData('text/plain')) }
                           onDragOver={ _=> { if (!setup.isInputHover) setSetup(setup => ({ ...setup, isInputHover: true })) }}
                           onDragLeave={ _=> setSetup(setup => ({ ...setup, isInputHover: false })) }
                        >
                           { findInput ? <span className="active">{ findInput.data.index }</span> : <span className="empty">Input (Empty)</span> }
                        </div>
                        <div className="dataflow"></div>
                        <button 
                           className="start"
                           disabled={ !setup.inputId || !setup.outputId } 
                           onClick={ _=> {
                              setSetup(setup => ({ ...setup, start: true }));
                              setTimeout(() => {
                                 if (messagesRef.current) messagesRef.current.scrollTo(0, messagesRef.current.scrollHeight);
                              }, 50);
                           }}
                        >
                           <FontAwesomeIcon icon={ faBolt } />
                           <span>Start</span>
                        </button>
                        <div
                           className={`block output ${ setup.isOutputHover ? 'focus' : '' }`} 
                           onDrop={ e => handleDropBlock('output', e.dataTransfer.getData('text/plain')) }
                           onDragOver={ _=> { if (!setup.isOutputHover) setSetup(setup => ({ ...setup, isOutputHover: true })) }}
                           onDragLeave={ _=> setSetup(setup => ({ ...setup, isOutputHover: false })) }
                        >
                           { findOutput ? <span className="active">{ findOutput.data.index }</span> : <span className="empty">Output (Empty)</span> }
                        </div>
                     {/* </>   
                  } */}</div>
               </div>
            </div> : <div className="content">
               <ul className="tabs">
                  <li 
                     className="add" 
                     onClick={ _=> { 
                        if (setup.isNodesUpdated || setup.isCanGetOutput || isLoading) return '';
                        setTabsChat(tabs => {
                           return [...tabs.map(t => ({...t, isActive: false})), { id: tabs.length+1, title: 'New Tab', isActive: true, items: [] }];
                        });
                     }}
                  >
                     <FontAwesomeIcon icon={ faPlus } />
                     <span>Add new tab</span>
                  </li>
                  { tabsChat.map(item => (
                     <li 
                        key={ item.id } 
                        className={`item ${item.isActive ? 'active' : ''}`} 
                        onClick={ _=> {
                           if (item.isActive || setup.isNodesUpdated || setup.isCanGetOutput) return '';
                           setTabsChat(tabs => tabs.map((tab) => ({ ...tab, isActive: tab.id === item.id, isTitleEdit: false })));
                        }}
                     > 
                        { item.isTitleEdit ? 
                        <input 
                           value={ titleTabValue } 
                           maxLength={ 30 } 
                           onChange={ e => setTitleTabValue(e.target.value) }
                           onKeyUp={ e => {
                              if (e.code === 'Enter' || e.code === 'NumpadEnter') {
                                 setTabsChat(tabs => tabs.map((tab) => ({ ...tab, title: tab.id === item.id ? titleTabValue : tab.title, isTitleEdit: false })));
                              }
                           }}
                        /> : 
                        <span>{ item.title }</span> }
                        <FontAwesomeIcon 
                           icon={ faPenToSquare } 
                           onClick={ e => {
                              e.stopPropagation();
                              setTitleTabValue(item.title);
                              setTabsChat(tabs => tabs.map((tab) => ({ 
                                 ...tab, isActive: tab.id === item.id, isTitleEdit: tab.id === item.id ? !tab.isTitleEdit : false 
                              })));
                           }} 
                        />
                     </li>
                  ))}
               </ul>
               <div className="chat">
                  <div className="title">
                     <h3>Chat Assistant Interface</h3>
                     { !isExternalChat ? <div onClick={ _=> window.open(`/workflow?id=${projectId}&isExternalChat=true&inputId=${setup.inputId}&outputId=${setup.outputId}`, '_blank') }>
                        <FontAwesomeIcon icon={ faArrowUpRightFromSquare } />
                        <span>External chat</span> 
                     </div> : null }
                  </div>
                  <div ref={ messagesRef } className="messages">{
                     tabActive?.items.length ? tabActive.items.map((item, index) => (
                        <div key={ index.toString() } className="item">
                           { item.isUser ? <img src="images/default_avatar.webp" alt="avatar" /> : <img src="images/logo2.jpg" alt="avatar" /> }
                           <pre>
                              <span>{ item.isUser ? userAuth?.fullName : outputPlug?.data.index }</span>
                              <span>{ item.text }</span>
                           </pre>
                        </div>
                     )) : <div className="empty">There is no conversation</div>
                  }</div>
                  <form className="action" onSubmit={ handleSendMessage }>
                     <textarea ref={ textareaRef } value={ messageValue } placeholder="Your message..." onChange={ e => setMessageValue(e.target.value) }></textarea>
                     <div>
                        { fileBlock ? <div 
                           className="file" 
                           style={{ 
                              backgroundColor: !fileBlock.data?.value?.current?.length && fileBlock.data?.value?.files?.length && fileBlock.data?.value?.files[fileBlock.data?.value?.files?.length - 1]?.name ? 'rgba(51, 153, 102, 0.15)' : 'rgba(30, 39, 46, 0.08)' 
                           }}
                        >
                           <input type="file" onChange={ handleFileChange } accept={ fileBlock.data.action === 'docSearch' ? allowedFileTypesDocSearch.join(',') : allowedFileTypesFileUploader.join(',') } multiple />
                           <FontAwesomeIcon icon={ faPaperclip } />
                           <span>
                              { fileBlock.data?.value?.current?.length ? fileBlock.data?.value?.current[0]?.name : (fileBlock.data?.value?.files?.length ? fileBlock.data?.value?.files[fileBlock.data?.value?.files?.length - 1]?.name : 'No file') }
                           </span>
                        </div> 
                        : null }
                        <button className={ setup.isNodesUpdated || setup.isCanGetOutput ? 'loading' : '' } disabled={ !messageValue }>
                           { setup.isNodesUpdated || setup.isCanGetOutput ? <AiOutlineLoading3Quarters /> : <FontAwesomeIcon icon={ faPaperPlane } /> }
                        </button>
                     </div>
                  </form>
               </div>
            </div> }
         </Main>
      </div>
   );
}